import { useEffect, useRef, useState } from "react";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDoorOpen,
  faWifi,
  faBatteryThreeQuarters,
  faSun,
  faTemperatureLow,
} from "@fortawesome/free-solid-svg-icons";
import ValveComponent from "./ValveComponent";
import Concrete_Bed from "./3D_irrigation_renders/Concrete_bed2.png.svg";
import Main_line_green from "./3D_irrigation_renders/Main_line_red.svg";
import main_line_red from "./3D_irrigation_renders/Main_line_Red.svg.svg";
import pressuremeter from "./3D_irrigation_renders/Pressure_Metre.svg";
import Watermeter from "./3D_irrigation_renders/Water_Metre.svg";
import antenapole from "./3D_irrigation_renders/Pole_antenna.png";
import ProgressBar from "./ProgressBar";
import ScheduelPopup from "./ScheduelPopup";

const extractSovs = (data: any): boolean[] =>
  Array.from({ length: 14 }, (_, i) => !!data[`sov${i + 1}`]);

type MainPanel = "details" | "mode";
type ModePanel = "manual" | "flow" | "pressure";

const pillBtn = (active: boolean, disabled: boolean): React.CSSProperties => ({
  padding: "6px 18px",
  borderRadius: 999,
  border: "none",
  fontSize: 12,
  fontWeight: active ? 700 : 400,
  background: active ? "#2b5e5e" : "transparent",
  color: active ? "#fff" : "#2b5e5e",
  cursor: disabled ? "not-allowed" : "pointer",
  userSelect: "none",
  outline: "none",
  whiteSpace: "nowrap" as const,
  opacity: disabled ? 0.5 : 1,
});

const pillWrap: React.CSSProperties = {
  display: "inline-flex",
  background: "#e8f0f0",
  borderRadius: 999,
  padding: 3,
  gap: 2,
};

const cmdBtn = (bg: string, disabled: boolean): React.CSSProperties => ({
  padding: "10px 14px",
  fontSize: 12,
  borderRadius: 6,
  border: "none",
  background: disabled ? "#999" : bg,
  color: "#fff",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.7 : 1,
  transition: "all 0.2s",
  zIndex: 3,
});

// ── Control row ───────────────────────────────────────────────
const ControlRow = ({
  label,
  value,
  onChange,
  onSet,
  onGet,
  showGet,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onSet: () => void;
  onGet: () => void;
  showGet: boolean;
  disabled: boolean;
}) => (
  <div
    className="d-flex flex-column align-items-center"
    style={{ padding: "12px 10px", borderBottom: "1px solid #e8e8e8" }}
  >
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: "#2b5e5e",
        marginBottom: 8,
      }}
    >
      {label}
    </div>
    <div style={{ display: "flex", gap: 6 }}>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Value"
        disabled={disabled}
        style={{
          width: 80,
          padding: "5px 8px",
          fontSize: 12,
          border: "1px solid #ccc",
          borderRadius: 6,
          outline: "none",
          opacity: disabled ? 0.6 : 1,
        }}
      />
      <button
        onClick={onSet}
        disabled={disabled}
        style={cmdBtn("#2b5e5e", disabled)}
      >
        Set
      </button>
      {showGet && (
        <button
          onClick={onGet}
          disabled={disabled}
          style={cmdBtn("#89AC46", disabled)}
        >
          Get
        </button>
      )}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────

const ChackComponent = ({
  id,
  onClose,
}: {
  id: number;
  onClose: () => void;
}) => {
  const [device, setDevice] = useState<any>(null);
  const [sovList, setSovList] = useState<boolean[]>([]);

  const [mainPanel, setMainPanel] = useState<MainPanel>("details");
  const [modePanel, setModePanel] = useState<ModePanel>("manual");
  const [view, setView] = useState<"main" | "schedule">("main");

  const [flowValue, setFlowValue] = useState("");
  const [pressureValue, setPressureValue] = useState("");

  // ── Command loading state ─────────────────────────────────────
  const [cmdLoading, setCmdLoading] = useState(false);
  const [progNow, setProgNow] = useState(0);
  const progInterval = useRef<NodeJS.Timeout | null>(null);

  const gradientStyle: React.CSSProperties = {
    background: "#2b5e5e",
    padding: "6px 0px",
  };

  // ── Progress bar ──────────────────────────────────────────────
  const startProgress = () => {
    if (progInterval.current) clearInterval(progInterval.current);
    setProgNow(0);
    setCmdLoading(true);
    const increment = 100 / (25000 / 250);
    let current = 0;
    progInterval.current = setInterval(() => {
      current += increment;
      if (current >= 100) {
        current = 100;
        clearInterval(progInterval.current!);
        progInterval.current = null;
      }
      setProgNow(current);
    }, 250);
  };

  const stopProgress = () => {
    if (progInterval.current) {
      clearInterval(progInterval.current);
      progInterval.current = null;
    }
    setProgNow(0);
    setCmdLoading(false);
  };

  // ── Fetch device ──────────────────────────────────────────────
  const fetchDevice = async () => {
    try {
      const res = await ApiService.axiosApi(
        `DemoDevice/GetDemoDeviceByID/${id}`,
        { method: "GET" },
      );
      if (res.success) {
        setDevice(res.data);
        setSovList(extractSovs(res.data));
        const mode = res.data.mode;
        if (mode === 1) {
          setModePanel("manual");
        } else if (mode === 2) {
          setModePanel("flow");
          setFlowValue(String(res.data.modeValue ?? ""));
        } else if (mode === 3) {
          setModePanel("pressure");
          setPressureValue(String(res.data.modeValue ?? ""));
        }
      } else {
        toast.error("Failed to load device");
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchDevice();
  }, [id]);

  if (!device) return <div className="p-4 text-center">Loading...</div>;

  const activeSOVCount = sovList.filter(Boolean).length;
  const numberOfValves = device.numberOfValve ?? 14;
  const initialMode: ModePanel =
    device.mode === 1 ? "manual" : device.mode === 2 ? "flow" : "pressure";
  const showGet = modePanel !== "manual" && modePanel === initialMode;

  // ── Commands ──────────────────────────────────────────────────
  const handleSet = async (type: "manual" | "flow" | "pressure") => {
    const mode = type === "manual" ? 1 : type === "flow" ? 2 : 3;
    const value =
      type === "flow" ? flowValue : type === "pressure" ? pressureValue : "0";

    if (type !== "manual" && !value) {
      toast.error("Value enter karo");
      return;
    }

    startProgress();
    try {
      const res = await ApiService.axiosApi("Command/movm", {
        method: "POST",
        data: {
          deviceId: device.id,
          mode,
          value: type === "manual" ? 0 : parseFloat(value).toFixed(1), // 👈 "1.0", "2.0"
        },
      });
      if (res.success) toast.success(`✅ ${type} set`);
      else toast.error("Failed");
    } catch (e) {
      console.error(e);
    } finally {
      stopProgress();
    }
  };

  const handleGet = async () => {
    startProgress();
    try {
      const res = await ApiService.axiosApi(`Command/get/movm/${device.id}`, {
        method: "GET",
      });
      if (res.success) {
        const parts = (res.data.message ?? "").split(" ");
        const mode = parseInt(parts[1]);
        const val = parts[2] ?? "0";
        if (mode === 1) setModePanel("manual");
        else if (mode === 2) {
          setModePanel("flow");
          setFlowValue(val);
        } else if (mode === 3) {
          setModePanel("pressure");
          setPressureValue(val);
        }
        toast.success(
          `✅ Mode: ${mode === 1 ? "Manual" : mode === 2 ? "Flow" : "Pressure"}${mode !== 1 ? `, Value: ${val}` : ""}`,
        );
      } else toast.error("Failed");
    } catch (e) {
      console.error(e);
    } finally {
      stopProgress();
    }
  };

  const handleInterrogate = async () => {
    startProgress();
    try {
      const res = await ApiService.axiosApi("Command/intg", {
        method: "POST",
        data: { command: "INTG", deviceId: device.id },
      });
      if (res.success) {
        fetchDevice();
        toast.success("✅ Interrogate successfully");
      } else toast.error("Failed");
    } catch (e) {
      console.error(e);
    } finally {
      stopProgress();
    }
  };

  // handleScheduleAutoManual function add karo
  const handleScheduleAutoManual = async () => {
    startProgress();
    try {
      const res = await ApiService.axiosApi(
        "Command/UpdateScheduleAutoManual",
        {
          method: "POST",
          data: {
            deviceId: device.id,
            autoManual: !device.scheduleAuto, // toggle — false→true, true→false
          },
        },
      );
      if (res.success) {
        toast.success(
          `✅ Schedule ${device.scheduleAuto ? "Auto" : "Manual"} set`,
        );
        setTimeout(() => {
          fetchDevice();
        }, 600);
        fetchDevice(); // refresh device data
      } else {
        toast.error("Failed");
      }
    } catch (e) {
      console.error(e);
    } finally {
      stopProgress();
    }
  };

  return (
    <div className="modal-content d-xl-flex">
      {/* ── Header ── */}
      <div
        className="modal-header"
        style={{ ...gradientStyle, position: "relative" }}
      >
        {/* Progress bar — header ke bottom edge par */}
        {cmdLoading && (
          <ProgressBar
            totalpage={0}
            pageNumber={0}
            now={progNow}
            mystyle={{
              position: "absolute",
              top: "-23px",
              bottom: 0,
              left: 0,
              height: "4px",
              width: "100%",
              zIndex: 10,
              backgroundColor: "transparent",
              borderRadius: 0,
            }}
          />
        )}

        <div className="col-md-12">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h5 className="modal-title" style={{ color: "white" }}>
                <strong>{device.deviceName}</strong>
                <small className="ms-2" style={{ fontSize: 12, opacity: 0.8 }}>
                  {device.deviceType}
                </small>
              </h5>
            </div>
            <div className="col-md-6 d-flex justify-content-end gap-2 align-items-center px-4">
              <button
                disabled={cmdLoading}
                onClick={handleScheduleAutoManual}
                style={cmdBtn(
                  device.scheduleAuto ? "#F7374F" : "#89AC46",
                  cmdLoading,
                )}
                title={
                  device.scheduleAuto
                    ? "Manual mode — click for Auto"
                    : "Auto mode — click for Manual"
                }
              >
                {device.scheduleAuto ? "Sch Manual" : "Sch Auto"}
              </button>

              {/* Schedule toggle */}
              <button
                disabled={cmdLoading}
                onClick={() =>
                  setView(view === "schedule" ? "main" : "schedule")
                }
                style={cmdBtn("#3a7575", cmdLoading)}
              >
                {view === "schedule" ? "← Back" : "Schedule"}
              </button>

              {/* Interrogate */}
              <button
                disabled={cmdLoading}
                onClick={handleInterrogate}
                style={cmdBtn("#89AC46", cmdLoading)}
              >
                {cmdLoading ? "Sending..." : "Interrogate"}
              </button>

              {/* Close */}
              <button
                onClick={onClose}
                style={{
                  background: "none",
                  border: "none",
                  fontWeight: 700,
                  fontSize: 18,
                  cursor: "pointer",
                  color: "white",
                }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="modal-body" style={{ width: "100%" }}>
        {view === "schedule" ? (
          <ScheduelPopup
            totalValves={numberOfValves}
            ChakId={device.id}
            onBack={() => setView("main")}
          />
        ) : (
          <div className="row" style={{ width: "100%" }}>
            {/* ── Left icon strip ── */}
            <div
              className="col-md-1"
              style={{ borderRight: "0.5px inset grey" }}
            >
              <ul
                className="pop-icons justify-content-center align-items-center"
                style={{ listStyle: "none", padding: 0 }}
              >
                <li
                  className={`d-flex flex-column align-items-center pb-4 ${!device.door1 ? "green" : "red"}`}
                >
                  <FontAwesomeIcon icon={faDoorOpen} size="2x" />
                  <div style={{ fontSize: 11 }}>
                    {device.door1 ? "Open" : "Close"}
                  </div>
                </li>
                <li className={`d-flex flex-column align-items-center pb-4 ${device.networkStrength == 'Excellent'? 'green' : 
                device.networkStrength == 'good' ? 'green' :
                device.networkStrength == 'average' ? 'network-yellow' :
                 device.networkStrength == 'poor' ? 'red':''}`}>
                  <FontAwesomeIcon icon={faWifi} size="2x" />
                  <div style={{ fontSize: 11 }}>{device.networkStrength}</div>
                </li>
                <li
                  className={`${device.lowBatteryBit ? "red" : "green"} d-flex flex-column align-items-center pb-4`}
                >
                  <FontAwesomeIcon icon={faBatteryThreeQuarters} size="2x" />
                  <div style={{ fontSize: 11 }}>{device.batteryVoltage}V</div>
                </li>
                <li className="green d-flex flex-column align-items-center pb-4">
                  <FontAwesomeIcon icon={faSun} size="2x" />
                  <div style={{ fontSize: 11 }}>{device.solarVoltage}V</div>
                </li>
                <li
                  className={`${device.highTemperatureBit ? "red" : "green"} d-flex flex-column align-items-center`}
                >
                  <FontAwesomeIcon icon={faTemperatureLow} size="2x" />
                  <div style={{ fontSize: 11 }}>{device.temperature}°C</div>
                </li>
              </ul>
            </div>

            {/* ── Centre diagram ── */}
            <div className="col-md-8">
              <div id="valve-component">
                <img id="antena_pole" src={antenapole} alt="" />
                <img
                  id="Main_line_red"
                  src={activeSOVCount > 0 ? Main_line_green : main_line_red}
                  alt=""
                />
                <img id="Concrete_Bed" src={Concrete_Bed} alt="" />
                <img id="pressure-meter" src={pressuremeter} alt="" />
                <img id="water-meter" src={Watermeter} alt="" />

                {sovList
                  .slice(2)
                  .slice(0, numberOfValves)
                  .map((isOn, i) => (
                    <ValveComponent
                      key={i}
                      index={i + 2} // actual SOV: SOV3, SOV4...
                      displayIndex={i} // display: V1, V2...
                      isOn={isOn}
                      chakId={device.id}
                      romsAuto={false}
                      scheduleAuto={device.scheduleAuto ?? false}
                      onRefresh={fetchDevice}
                      isPressureorFlow={device.mode === 2 || device.mode === 3}
                    />
                  ))}
              </div>
            </div>

            {/* ── Right panel ── */}
            <div className="col-md-3">
              <div
                className="card z-1"
                style={{
                  width: "100%",
                  border: mainPanel === "mode" ? "1px solid #2b5e5e" : "none",
                }}
              >
                {/* Level 1 pill */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    backgroundColor: "rgba(43,94,94,0.1)",
                    borderRadius: 5,
                    paddingTop: 5,
                    paddingBottom: 5,
                  }}
                >
                  <div style={pillWrap}>
                    <button
                      style={pillBtn(mainPanel === "details", false)}
                      onClick={() => setMainPanel("details")}
                    >
                      Details
                    </button>
                    <button
                      style={pillBtn(mainPanel === "mode", false)}
                      onClick={() => setMainPanel("mode")}
                    >
                      Mode
                    </button>
                  </div>
                </div>

                {/* Level 2 pill */}
                {mainPanel === "mode" && (
                  <div
                    style={{
                      padding: "8px 10px 0",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <div style={pillWrap}>
                      <button
                        style={pillBtn(modePanel === "manual", cmdLoading)}
                        disabled={cmdLoading}
                        onClick={() => setModePanel("manual")}
                      >
                        Manual
                      </button>
                      <button
                        style={pillBtn(modePanel === "flow", cmdLoading)}
                        disabled={cmdLoading}
                        onClick={() => setModePanel("flow")}
                      >
                        Flow
                      </button>
                      <button
                        style={pillBtn(modePanel === "pressure", cmdLoading)}
                        disabled={cmdLoading}
                        onClick={() => setModePanel("pressure")}
                      >
                        Pressure
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 8 }}>
                  {/* Details */}
                  {mainPanel === "details" && (
                    <>
                      <div
                        className="card-header px-3 py-2 d-flex justify-content-between"
                        style={gradientStyle}
                      >
                        <h6
                          className="card-title mb-0"
                          style={{ color: "white" }}
                        >
                          Details
                        </h6>
                        <div style={{ fontSize: 11, color: "white" }}>
                          {new Date(device.serverDateTime).toLocaleString(
                            "en-IN",
                          )}
                        </div>
                      </div>
                      <div className="card-body p-0">
                        <table
                          className="table table-hover mb-0"
                          style={{ fontSize: 13 }}
                        >
                          <tbody>
                            <tr
                              style={{ backgroundColor: "rgba(43,94,94,0.1)" }}
                            >
                              <th>Device</th>
                              <td>{device.deviceName}</td>
                            </tr>
                            <tr>
                              <td>Type</td>
                              <td>{device.deviceType}</td>
                            </tr>
                            <tr
                              style={{ backgroundColor: "rgba(43,94,94,0.1)" }}
                            >
                              <td>Pressure 1</td>
                              <td>{device.pressureTransmitter1} Bar</td>
                            </tr>
                            <tr>
                              <td>Pressure 2</td>
                              <td>{device.pressureTransmitter2} Bar</td>
                            </tr>
                            <tr
                              style={{ backgroundColor: "rgba(43,94,94,0.1)" }}
                            >
                              <td>Water Meter 1</td>
                              <td>{device.waterMeterValue1}</td>
                            </tr>
                            <tr>
                              <td>Water Meter 2</td>
                              <td>{device.waterMeterValue2}</td>
                            </tr>
                            <tr
                              style={{ backgroundColor: "rgba(43,94,94,0.1)" }}
                            >
                              <td>SOVs ON</td>
                              <td>
                                {activeSOVCount} / {numberOfValves}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {/* Manual */}
                  {mainPanel === "mode" && modePanel === "manual" && (
                    <div style={{ padding: "12px 10px" }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#2b5e5e",
                          marginBottom: 8,
                        }}
                      >
                        Manual Mode
                      </div>
                      <button
                        disabled={cmdLoading}
                        onClick={() => handleSet("manual")}
                        style={cmdBtn("#2b5e5e", cmdLoading)}
                      >
                        {cmdLoading ? "Sending..." : "Set Manual"}
                      </button>
                    </div>
                  )}

                  {/* Flow */}
                  {mainPanel === "mode" && modePanel === "flow" && (
                    <ControlRow
                      label="Flow Rate"
                      value={flowValue}
                      onChange={setFlowValue}
                      onSet={() => handleSet("flow")}
                      onGet={handleGet}
                      showGet={showGet}
                      disabled={cmdLoading}
                    />
                  )}

                  {/* Pressure */}
                  {mainPanel === "mode" && modePanel === "pressure" && (
                    <ControlRow
                      label="Pressure"
                      value={pressureValue}
                      onChange={setPressureValue}
                      onSet={() => handleSet("pressure")}
                      onGet={handleGet}
                      showGet={showGet}
                      disabled={cmdLoading}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChackComponent;
